import { useCallback, useEffect, useState } from 'react';

/**
 * useGuideLang — shared bilingual toggle for the guides & info section.
 *
 * Iter-51.13: previously the guide pages (HowToUse, WhoIsThisFor,
 * Referral & Rewards, T&C, Data & Privacy, Upload guidelines) rendered
 * EN and HI stacked one under the other. That produced visual clutter
 * and doubled every page's height on mobile. Now each of those pages
 * / sections shows ONE language at a time, driven by a single shared
 * toggle whose selection persists in localStorage so a returning user
 * sees their preferred language without another tap.
 *
 * The main READING pipeline (chunks, feedback, tap-to-hear) is NOT
 * affected — that continues to use per-child language settings from
 * the AppContext. This hook is only for informational / marketing
 * copy across the guide surfaces.
 */
const LS_KEY = 'readbuddy.guides.lang';

export function getGuideLang() {
  try {
    const v = window.localStorage.getItem(LS_KEY);
    return v === 'hi' ? 'hi' : 'en';
  } catch {
    return 'en';
  }
}

export function setGuideLang(lang) {
  const normalized = lang === 'hi' ? 'hi' : 'en';
  try { window.localStorage.setItem(LS_KEY, normalized); } catch {}
  // Broadcast so every mounted GuideLangToggle / consumer updates
  // simultaneously — including across accordions on the same page.
  try {
    window.dispatchEvent(new CustomEvent('guides-lang-changed', { detail: normalized }));
  } catch {}
}

export default function useGuideLang() {
  const [lang, setLangState] = useState(getGuideLang);

  useEffect(() => {
    const onChange = (e) => setLangState(e.detail === 'hi' ? 'hi' : 'en');
    window.addEventListener('guides-lang-changed', onChange);
    // Cross-tab sync via native `storage` event (e.g. user changes it
    // in another tab).
    const onStorage = (e) => {
      if (e.key === LS_KEY) setLangState(e.newValue === 'hi' ? 'hi' : 'en');
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('guides-lang-changed', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const set = useCallback((next) => setGuideLang(next), []);
  return [lang, set];
}
